
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { AspectRatio } from '@/components/ui/aspect-ratio';
import { Button } from '@/components/ui/button';
import { ShoppingCart, ChevronLeft, MessageSquare } from 'lucide-react';
import { useCartStore } from '@/store/useCartStore';
import { formatCurrency, calculateDiscountedPrice } from '@/lib/utils';
import { Badge } from '@/components/ui/badge';
import Layout from '@/components/layout/Layout';
import { useToast } from '@/hooks/use-toast';
import ProductRating from '@/components/product/ProductRating';
import ProductReviews from '@/components/product/ProductReviews';
import { useProducts } from '@/hooks/useProducts';

// Function to generate a consistent rating based on product ID
const getProductRating = (id: string): number => {
  const charSum = id.split('').reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return Math.min(5, 3 + (charSum % 20) / 10); // Rating between 3.0 and 5.0
};

// Function to generate review count based on product ID
const getReviewCount = (id: string): number => {
  const lastDigit = parseInt(id.charAt(id.length - 1), 36) % 10;
  return 5 + lastDigit * 12; // Between 5 and 113 reviews
};

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { addItem } = useCartStore();
  const { toast } = useToast();
  const [quantity, setQuantity] = useState(1);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [showReviews, setShowReviews] = useState(false);
  const { fetchProduct } = useProducts();
  const [product, setProduct] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Scroll to top when component mounts
    window.scrollTo(0, 0);
    
    const loadProduct = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        const productData = await fetchProduct(id);
        
        if (productData) {
          setProduct({
            id: productData.id,
            name: productData.name,
            price: productData.price,
            currency: 'RWF',
            image: productData.imageUrl,
            images: productData.images || [productData.imageUrl],
            description: productData.description,
            discount: productData.discount,
            couponCode: productData.couponCode,
            category: productData.category,
            subcategory: productData.subcategory,
          });
        } else {
          setError('Product not found');
        }
      } catch (err) {
        console.error(`Error loading product with ID ${id}:`, err);
        setError('Failed to load product details');
      } finally {
        setLoading(false);
      }
    };
    
    loadProduct();
  }, [id, fetchProduct]);

  // If product is loading, show a loading state
  if (loading) {
    return <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-7xl mx-auto">
          <div className="animate-pulse">
            <div className="h-6 bg-gray-200 rounded w-1/4 mb-6"></div>
            <div className="flex flex-col md:flex-row gap-8">
              <div className="w-full md:w-1/2">
                <div className="bg-gray-200 rounded aspect-square mb-4"></div>
                <div className="grid grid-cols-4 gap-2">
                  {[1, 2, 3, 4].map(i => (
                    <div key={i} className="bg-gray-200 rounded aspect-square"></div>
                  ))}
                </div>
              </div>
              <div className="w-full md:w-1/2">
                <div className="h-8 bg-gray-200 rounded w-3/4 mb-4"></div>
                <div className="h-4 bg-gray-200 rounded w-1/4 mb-6"></div>
                <div className="h-6 bg-gray-200 rounded w-1/3 mb-8"></div>
                <div className="h-24 bg-gray-200 rounded mb-8"></div>
                <div className="h-10 bg-gray-200 rounded w-1/3 mb-4"></div>
                <div className="h-12 bg-gray-200 rounded w-1/2"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>;
  }

  // If product not found, show a message
  if (error || !product) {
    return <Layout>
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-2xl font-bold mb-4">Product Not Found</h1>
          <p className="mb-6">Sorry, we couldn't find the product you're looking for.</p>
          <Link to="/">
            <Button>
              <ChevronLeft className="mr-2 h-4 w-4" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </Layout>;
  }

  const originalPrice = product.price;
  const discountedPrice = product.discount ? calculateDiscountedPrice(originalPrice, product.discount) : originalPrice;
  const rating = getProductRating(product.id);
  const reviewCount = getReviewCount(product.id);

  const handleAddToCart = () => {
    addItem({
      id: product.id,
      name: product.name,
      price: product.price.toString(),
      currency: product.currency,
      image: product.image,
      quantity: quantity,
      discount: product.discount
    });
    toast({
      title: "Added to cart",
      description: `${product.name} has been added to your cart.`
    });
  };

  const handleQuantityChange = (newQuantity: number) => {
    if (newQuantity >= 1) {
      setQuantity(newQuantity);
    }
  };

  const toggleReviews = () => {
    setShowReviews(!showReviews);
  };

  // Make sure we have images array
  const productImages = product.images?.length ? product.images : [product.image];

  return <Layout>
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <div className="mb-4">
          <Link to="/" className="text-sm text-gray-500 hover:text-ahavex-blue inline-flex items-center py-[13px]">
            <ChevronLeft className="h-4 w-4 mr-1" />
            Back to Products
          </Link>
        </div>
        
        <div className="flex flex-col md:flex-row gap-8">
          {/* Product Images */}
          <div className="w-full md:w-1/2 space-y-4">
            <div className="border border-gray-200 bg-white">
              <AspectRatio ratio={1 / 1}>
                <img src={productImages[activeImageIndex]} alt={product.name} className="w-full h-full object-cover" />
              </AspectRatio>
            </div>
            
            {/* Thumbnails */}
            <div className="grid grid-cols-4 gap-2">
              {productImages.map((img: string, index: number) => (
                <div 
                  key={index} 
                  className={`border cursor-pointer ${activeImageIndex === index ? 'border-ahavex-blue' : 'border-gray-200'}`} 
                  onClick={() => setActiveImageIndex(index)}
                >
                  <AspectRatio ratio={1 / 1}>
                    <img src={img} alt={`${product.name} - view ${index + 1}`} className="w-full h-full object-cover" />
                  </AspectRatio>
                </div>
              ))}
            </div>
          </div>
          
          {/* Product Details */}
          <div className="w-full md:w-1/2">
            <h1 className="text-2xl md:text-3xl font-bold mb-2">{product.name}</h1>
            
            {/* Product Rating */}
            <div className="flex items-center gap-2 mb-3">
              <ProductRating rating={rating} />
              <span className="text-sm text-gray-600">
                ({reviewCount} reviews)
              </span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-xs text-ahavex-blue" 
                onClick={toggleReviews}
              >
                <MessageSquare className="h-3 w-3 mr-1" />
                {showReviews ? 'Hide Reviews' : 'View Reviews'}
              </Button>
            </div>
            
            <div className="flex items-center gap-3 mb-4">
              {product.discount ? (
                <>
                  <div className="text-xl font-bold text-ahavex-blue">
                    {formatCurrency(discountedPrice, product.currency)}
                  </div>
                  
                  <div className="text-gray-500 line-through">
                    {formatCurrency(originalPrice, product.currency)}
                  </div>
                  
                  <Badge className="bg-red-600">-{product.discount}% OFF</Badge>
                </>
              ) : (
                <div className="text-xl font-bold text-ahavex-blue">
                  {formatCurrency(originalPrice, product.currency)}
                </div>
              )}
              
              {product.couponCode && (
                <Badge className="bg-blue-600">Code: {product.couponCode}</Badge>
              )}
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">{product.description}</p>
            </div>
            
            <div className="space-y-4">
              <div className="flex items-center space-x-4">
                <span className="text-gray-700">Quantity:</span>
                <div className="flex items-center border border-gray-300">
                  <button onClick={() => handleQuantityChange(quantity - 1)} className="px-3 py-1 border-r border-gray-300" aria-label="Decrease quantity" disabled={quantity <= 1}>
                    -
                  </button>
                  <span className="px-4 py-1">{quantity}</span>
                  <button onClick={() => handleQuantityChange(quantity + 1)} className="px-3 py-1 border-l border-gray-300" aria-label="Increase quantity">
                    +
                  </button>
                </div>
              </div>
              
              <Button onClick={handleAddToCart} className="w-full md:w-auto px-8 py-6 text-base" size="lg">
                <ShoppingCart className="mr-2 h-4 w-4" />
                Add to Cart
              </Button>
              
              <div className="pt-6 border-t border-gray-200">
                <h3 className="text-lg font-medium mb-2">Product Details</h3>
                <ul className="list-disc list-inside space-y-1 text-gray-700">
                  {product.category && (
                    <li>Category: {product.category}</li>
                  )}
                  {product.subcategory && (
                    <li>Subcategory: {product.subcategory}</li>
                  )}
                  <li>High-quality product</li>
                  <li>Fast shipping available</li>
                  <li>30-day return policy</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
        
        {/* Reviews Section */}
        {showReviews && (
          <div className="mt-8 border-t border-gray-200 pt-6">
            <ProductReviews productId={product.id} productName={product.name} />
          </div>
        )}
      </div>
    </Layout>;
};

export default ProductDetail;
